import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useAccountId } from "../../../auth/useAccountId";
import useStore from "../../../common/hooks/useExternalStore";
import createExternalStore from "../../../common/utils/createExternalStore";
import { CORE_API_HOSTNAME } from "../../../env";
import fulfill from "../../../fulfill";
import type { PersistedInsightReportConfig } from "../../../types/insight";

type State = {
    configurationToDelete: PersistedInsightReportConfig | undefined;
    showNewConfigurationModal: boolean;
    createError: undefined | "bad_request" | "conflict";
};

const initialState: State = {
    configurationToDelete: undefined,
    showNewConfigurationModal: false,
    createError: undefined,
};

const fetchConfigurations = async (payload: {
    accountId: string;
}): Promise<PersistedInsightReportConfig[]> => {
    return await fulfill.get({
        accountId: payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${payload.accountId}/insight/reports/configuration`,
        handlers: {
            200: (data) => data,
        },
    });
};

const removeConfiguration = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            configurationId: string;
        };
    },
): Promise<PersistedInsightReportConfig> => {
    return await fulfill.delete({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/insight/reports/configuration/${options.arg.configurationId}`,
        handlers: {
            200: (data) => data,
        },
    });
};

const postNewConfiguration = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            configuration: PersistedInsightReportConfig;
        };
    },
): Promise<PersistedInsightReportConfig> => {
    return await fulfill.post({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/insight/reports/configuration/`,
        json: options.arg.configuration,
        handlers: {
            200: (data) => data,
            409: () => {
                throw new Error("conflict");
            },
            400: () => {
                throw new Error("bad_request");
            },
        },
    });
};

const reportsStore = createExternalStore(
    initialState,
    {
        toggleDeleteModal: (
            state,
            { configuration }: { configuration: PersistedInsightReportConfig },
        ) => {
            return {
                ...state,
                configurationToDelete: configuration,
            };
        },
        toggleNewModal: (
            state,
            {
                showNewConfigurationModal,
            }: { showNewConfigurationModal: boolean },
        ) => {
            return {
                ...state,
                showNewConfigurationModal,
                createError: undefined,
            };
        },
        setCreateError: (
            state,
            { error }: { error: "bad_request" | "conflict" },
        ) => {
            return {
                ...state,
                createError: error,
            };
        },
        resetStore: () => initialState,
    },
    "settings/reports",
);

export const useReports = () => {
    const accountId = useAccountId();
    const { state, dispatch } = useStore(reportsStore);
    const { data, isLoading, mutate } = useSWR(
        () =>
            accountId
                ? {
                      accountId,
                      key: `${accountId}/insight-reports`,
                  }
                : null,
        fetchConfigurations,
    );
    const deleteConfigurationActions = useSWRMutation(
        () => (accountId ? `${accountId}/insight-reports` : null),
        removeConfiguration,
        {
            onSuccess: () => {
                dispatch("toggleDeleteModal", { configuration: undefined });
                mutate();
            },
        },
    );
    const postNewConfigurationActions = useSWRMutation(
        () => (accountId ? `${accountId}/insight-reports` : null),
        postNewConfiguration,
        {
            onError: (error) => {
                if (error instanceof Error) {
                    dispatch("setCreateError", {
                        error: error.message as "bad_request" | "conflict",
                    });
                }
            },
            onSuccess: () => {
                dispatch("toggleNewModal", {
                    showNewConfigurationModal: false,
                });
                mutate();
            },
        },
    );

    return {
        configurations: data || [],
        isLoading:
            isLoading ||
            deleteConfigurationActions.isMutating ||
            postNewConfigurationActions.isMutating,
        removeConfiguration: async (configurationId: string) => {
            return await deleteConfigurationActions.trigger({
                accountId,
                configurationId,
            });
        },
        openDeleteModal: (configuration: PersistedInsightReportConfig) => {
            dispatch("toggleDeleteModal", { configuration });
        },
        closeDeleteModal: () => {
            dispatch("toggleDeleteModal", { configuration: undefined });
        },
        showNewConfigurationModal: state.showNewConfigurationModal,
        openNewModal: () => {
            dispatch("toggleNewModal", { showNewConfigurationModal: true });
        },
        closeNewModal: () => {
            dispatch("toggleNewModal", { showNewConfigurationModal: false });
        },
        postNewConfiguration: async (
            configuration: PersistedInsightReportConfig,
        ) => {
            return await postNewConfigurationActions.trigger({
                accountId,
                configuration,
            });
        },
        createError: state.createError,
        configurationToDelete: state.configurationToDelete,
        resetStore: () => dispatch("resetStore"),
    };
};
